import { BottomSheetTabsListItemProps } from '@/components/molecules/bottom-sheet/bottom-sheet-tabs/bottom-sheet-tabs-list';
import { Tabs } from '@/components/molecules/tabs';
import { ReviewTagExposure, ReviewTagVo } from '@/models/review/review';
import {
  AgreementType,
  AiAnswerButtonType,
  AiAnswerStatus,
  AttachmentBlindType,
  GenderType,
  GetBestQuestionMobileCardsQuery,
  GetBestTagsQuery,
  GetCurationListQuery,
  GetMagazineListQuery,
  GetMyAnswerCursorPageQuery,
  GetNewestQuestionCardCursorPageQuery,
  GetNewsMyQuestionQuery,
  GetNewsQuestionQuery,
  GetQnaCategoryGroupQuery,
  PaymentTotalStatus,
  QuestionAccessibleType,
  QuestionAnsweredStatus,
  QuestionStatus,
} from '@/types/bff/gql-schema';

import { Nullable } from './common';
import { EQuestionAnsweredStatus } from './search';

export const QnaCategoryType = {
  TREATMENT: 'treatment',
  DEPARTMENT: 'department',
};
export type QnaCategoryType = (typeof QnaCategoryType)[keyof typeof QnaCategoryType];
export interface Attachment {
  blindType: AttachmentBlindType;
  questionAttachmentId: string;
  src: string;
  thumbnail: string;
}

export interface Answer {
  content: string;
  createdAt: number;
  footer?: string | null;
  header?: string | null;
  answerer: {
    affiliatedInstitutionName: string;
    answererId: string;
    answererType: AnswererType;
    name: string;
    profileImageUrl: string;
  };
  recommendedMagazineGraph?: {
    description: string;
    magazinePid: string;
    title: string;
    titleImageUrl: string;
  } | null;
  answererName: string;
  date: string;
}

export interface QnaQuestionDetailItemVo {
  status: QuestionStatus;
  accessibleType: QuestionAccessibleType;
  answeredStatus: QuestionAnsweredStatus;
  answererCount: number;
  answererProfileImageUrls: Array<string>;
  content: string;
  createdAt: number;
  likeCount: number;
  questionPid: string;
  showUserPersonalInfo: boolean;
  title: string;
  viewCount: number;
  category: { name: string; type: string; questionCategoryId: string };
  tags: Array<{ name: string; questionTagId: string }>;
  user: { name: string; birth?: string | null; gender: GenderType };
  attachments: Array<Attachment>;
  writer: string;
  date: string;
  answers: Array<Answer>;
  paymentInfo?: Nullable<QuestionPaymentInfo>;
  paymentTotal?: Nullable<QuestionPaymentTotal>;
}

export interface QuestionPaymentInfo {
  discountAmount: number;
  paidAmount: number;
  paidAt: number;
  payTryCount: number;
  price: number;
  usedPoint: number;
}

export interface QuestionPaymentTotal {
  currentAmount: number;
  payTrackingNumber: string;
  reason?: Nullable<string>;
  status: PaymentTotalStatus;
  userPaymentId?: Nullable<string>;
  userPayment?: Nullable<{
    cardName: string;
    cardNumber: string;
    id: string;
  }>;
}
export interface QnaNearSimpleQuestionsVo {
  questionPid: string;
  title: string;
}
export interface QnaQuestionDetail {
  question: QnaQuestionDetailItemVo;
  nearSimpleQuestions: Array<QnaNearSimpleQuestionsVo>;
  similarSearchQuestionCardCursorPageRequest: SimilarSearchQuestions;
}

export interface QnaCurationItem {
  questionPid: string;
  title: string;
}

export interface SimilarSearchQuestions {
  elements: {
    question: QnaCurationItem;
  }[];
}

export interface MagazineCuration extends Partial<GetCurationListQuery['magazineCursorPage']['elements'][0]> {
  createdDate: string;
}
export interface QnaMagazineCurations {
  curations: Omit<GetCurationListQuery['magazineCursorPage'], 'elements'> & {
    magazines: Array<MagazineCuration>;
  };
}

export interface MagazineCurationItem {
  title: string;
  image: string;
  date: string | null;
  magazinePid: string;
}

export interface QnaMagazineAuthor {
  affiliatedInstitutionName: string;
  externalLinkUrl?: string | null;
  name: string;
  profileImageUrl: string;
  professionalFields: Array<{
    name: string;
  }>;
}
export interface Magazine extends Partial<GetMagazineListQuery['magazineCursorPage']['elements'][0]> {
  createdDate: string;
}

export interface ImagePath {
  originalPath: string;
  thumbnailPath: string;
}

export interface RealtimeAnswerStatusTypeVo {
  questionPid: string;
  content: string;
  answeredStatus: QuestionAnsweredStatus;
}
// [Mobile] 방금 올라온 질문 -> 내 질문
type QnaNewestMyQuestionDto = GetNewsMyQuestionQuery['myQuestionCardCursorPage']['elements'][0];

export type QnaNewestMyQuestion = QnaNewestMyQuestionDto;

// [Mobile] 방금 올라온 질문
type QnaNewestQuestionDto = GetNewsQuestionQuery['newestQuestionCardCursorPage']['elements'][0];
export type QnaNewestQuestion = QnaNewestQuestionDto;

export interface QnaNewestQuestions {
  questions: Array<QnaNewestQuestion>;
}

export type QnaAttachment = {
  blindType: AttachmentBlindType;
  questionAttachmentId: string;
};

export type QnaBestTags = GetBestTagsQuery;
export type QnaBestTag = GetBestTagsQuery['bestTags'][0];
export type QnaBestQuestions = GetBestQuestionMobileCardsQuery;

export type QnaMyQuestionAnswer = GetMyAnswerCursorPageQuery['myAnswerCursorPage']['elements'][0];
export interface QnaMyQuestionAnswers {
  answers: Array<QnaMyQuestionAnswer>;
  isMore: boolean;
}

export interface QnaCategoryForBottomSheetTabsListItem {
  key: string;
  name: string;
  categories: Array<BottomSheetTabsListItemProps>;
}

export type QnaCategoryTag = GetQnaCategoryGroupQuery['questionCategoryGroups'][0]['questionCategories'][0]['tags'][0];

type QnaNewestQuestionCardDto = GetNewestQuestionCardCursorPageQuery['newestQuestionCardCursorPage']['elements'][0];

export interface QnaQuestionMobileCard extends QnaNewestQuestionCardDto {
  questionCreatedAt: string;
  answerCreatedAt: Nullable<string>;
  answeredStatusText: string;
  paymentTotal?: Nullable<QuestionPaymentTotal>;
}
export type QnaSearchKeywordList = Array<string>;

export interface ImagePath {
  originalPath: string;
  thumbnailPath: string;
}

export interface AddQuestionResponse {
  addQuestionV2: {
    aiAnswerAvailable: boolean;
    questionPid: string;
  };
}

export interface QuestionDetailWithAiAnswerAttachment {
  blindType: AttachmentBlindType;
  questionAttachmentId: string;
}
export interface QuestionDetailWithAiAnswerDto {
  content: string;
  questionPid: string;
  title: string;
  aiAnswerStatus: AiAnswerStatus;
  attachments: Array<QuestionDetailWithAiAnswerAttachment>;
  category: { name: string };
  aiAnswer?: {
    aiAnswerStatus: AiAnswerStatus;
    content: string;
    questionPid: string;
  } | null;
  aiAnswerButtonType: AiAnswerButtonType;
  pregnant: boolean;
}

export enum AnswerLikeType {
  Dislike = 'DISLIKE',
  Like = 'LIKE',
}

export enum AnswererType {
  Ai = 'AI',
  Doctor = 'DOCTOR',
  Nurse = 'NURSE',
}

export enum ProfessionalFieldType {
  Department = 'DEPARTMENT',
}

export enum NpsCompleteType {
  Satisfied = 'satisfied',
  UnSatisfied = 'unSatisfied',
}

export enum QnaWriteBottomSheetType {
  Default = 'Default',
  Pregnant = 'pregnant',
  Attachments = 'attachments',
  Reject = 'reject',
}

export interface QnaDetailAffiliation {
  address?: string | null;
  affiliationPid: string;
  imageUrl?: string | null;
  name: string;
}
export interface QnaDetailAnswerer {
  affiliatedInstitutionName: string;
  answererId: string;
  answererPublicId?: string | null;
  answererType: AnswererType;
  name: string;
  profileImageUrl: string;
  affiliation?: QnaDetailAffiliation | null;
}

export interface QnaDetailRecommendedMagazine {
  description: string;
  magazinePid: string;
  title: string;
  titleImageUrl: string;
}
export interface QnaDetailAnswerVo {
  answerPid: string;
  content: string;
  createdAt: number;
  dislikeCount: number;
  footer?: string | null;
  header?: string | null;
  isBlockedByMe: boolean;
  likeCount: number;
  myLikeType?: AnswerLikeType | null;
  answerer: QnaDetailAnswerer;
  recommendedMagazineGraph?: QnaDetailRecommendedMagazine | null;
  createdDate: string;
  reviews: Array<QnaAnswerReviewVo>;
  hasReview: boolean;
  isReviewViewable: boolean;
}
export interface QnaDetailVo {
  answererCount: number;
  answeredStatus: QuestionAnsweredStatus;
  content: string;
  createdAt: number;
  isBlockedByMe: boolean;
  isLikedByMe: boolean;
  isMine: boolean;
  likeCount: number;
  questionPid: string;
  showUserPersonalInfo: boolean;
  title: string;
  answers: Array<QnaDetailAnswerVo>;
  attachments: Array<{
    blindType: AttachmentBlindType;
    questionAttachmentId: string;
  }>;
  category: { name: string; questionCategoryId: string; type: string };
  tags: Array<{ name: string }>;
  user: {
    name: string;
    userId: string;
    birth?: string | null;
    gender: GenderType;
  };
  nameAndDate: string;
  isNotAnswered: boolean;
  nearSimpleQuestions: {
    prev?: string;
    next?: string;
  };
  paymentInfo?: Nullable<QuestionPaymentInfo>;
  paymentTotal?: Nullable<QuestionPaymentTotal>;
  status: QuestionStatus;
  accessibleType: QuestionAccessibleType;
  isAiAnswerChecked?: boolean;
}

export interface RelatedQnaVo {
  answeredStatus: EQuestionAnsweredStatus;
  answererCount: number;
  answererProfileImageUrls: Array<string>;
  question: {
    status?: QuestionStatus | null;
    title: string;
    content: string;
    questionPid: string;
    createdAt: number;
    category: {
      name: string;
      iconImageUrl: string;
      questionCategoryId: string;
    };
    attachments: Array<{
      blindType: AttachmentBlindType;
      questionAttachmentId: string;
    }>;
    tags: Array<{
      name: string;
    }>;
    isBlockedByMe: boolean;
  };
  answeredStatusText: string;
  questionCreatedAt: string;
}

export interface RealtimeQnaCardVo {
  answeredStatus: QuestionAnsweredStatus;
  answererCount: number;
  answererProfileImageUrls: Array<string>;
  answer?: {
    answerPid: string;
    content: string;
    createdAt: number;
    dislikeCount: number;
    footer?: string | null;
    header?: string | null;
    isBlockedByUser: boolean;
    likeCount: number;
    myLikeType?: AnswerLikeType | null;
    answerer: {
      affiliatedInstitutionName: string;
      answererId: string;
      answererType: AnswererType;
      name: string;
      profileImageUrl: string;
      professionalFields: Array<{
        name: string;
        professionalFieldId: string;
        professionalFieldType: ProfessionalFieldType;
      }>;
    };
  } | null;
  question: {
    accessibleType: QuestionAccessibleType;
    content: string;
    createdAt: number;
    isBlockedByMe: boolean;
    isLikedByMe: boolean;
    isMine: boolean;
    likeCount: number;
    questionPid: string;
    title: string;
    attachments: Array<{
      blindType: AttachmentBlindType;
      questionAttachmentId: string;
    }>;
    category: {
      name: string;
      questionCategoryId: string;
      iconImageUrl: string;
    };
    tags: Array<{ name: string }>;
    user: { name: string; userId: string };
  };
  questionCreatedAt: string;
  answerCreatedAt: Nullable<string>;
  answeredStatusText: string;
}

export interface QnaCategoryGroup {
  tabs: Tabs<QnaCategoryType>;
  tabsList: Array<QnaCategoryForBottomSheetTabsListItem>;
  categories: Array<BottomSheetTabsListItemProps>;
}

export interface AgreementByLocationCategory {
  id: string;
  title: string;
  url?: string | null;
}

export interface Policy {
  id: string;
  required: boolean;
  title: string;
  url?: string;
  type: AgreementType;
}

export interface QnaAnswerWriteReviewQueries {
  id?: string;
  answerPid?: string;
}

export interface QnaAnswerWriteReviewFormData {
  rating: number;
  content?: string;
  reviewTagIds: Array<string>;
}

export interface QnaAnswerCompleteReviewProps {
  title: string;
  description: string;
  onMoveQnaDetail: () => void;
}

export enum QnaAnswerWriteReviewStatus {
  RatingReview = 'RATING_REVIEW',
  SatisfactionReview = 'SATISFACTION_REVIEW',
  CompleteReview = 'COMPLETE_REVIEW',
}

export interface QnaAnswerReviewVo {
  id: string;
  answerPid: string;
  reviewTags: ReviewTagVo[];
  rating: number;
  content: string;
  createdAt: string;
  updatedAt: string;
}

export interface QnaAnswerReviewTagExposureTypeVo {
  id: string;
  text: string;
  exposureType: ReviewTagExposure;
}
